import * as Sentry from '@sentry/nextjs';
import { useCallback } from 'react';

import { isProduction } from '@/helpers/common';
import { useCommonStore } from '@/store';

enum CustomerIOMode {
  ON,
  OFF,
}

type CustomerIOMap = {
  [key in CustomerIOMode]: {
    identifyUser: (id: number) => void;
    resetUser: () => void;
    handleEvents: () => void;
    trackEvent: (eventName: string, data: Record<string, unknown>) => void;
  };
};

/**
 * IN - APP - for users who was logged in
 */
export const customerIOMap: CustomerIOMap = {
  [CustomerIOMode.ON]: {
    identifyUser: (id: number) => {
      try {
        // @ts-ignore
        _cio.identify({
          id: `levelupcasino:${id}`,
        });
      } catch {
        Sentry.addBreadcrumb({
          message: 'CustomerIO identifyUser failed',
          category: 'customerIO',
          level: 'error',
        });

        console.error('CustomerIO identifyUser failed');
      }
    },
    resetUser: () => {
      try {
        // @ts-ignore
        _cio.reset();
      } catch {
        Sentry.addBreadcrumb({
          message: 'CustomerIO resetUser failed',
          category: 'customerIO',
          level: 'error',
        });

        console.error('CustomerIO resetUser failed');
      }
    },
    handleEvents: () => {
      try {
        // @ts-ignore
        _cio?.on('in-app:message-action', (event) => {
          if (event.detail.action === 'open_intercom') {
            // @ts-ignore
            window.Intercom('show');
            event.detail.message.dismiss();
          }
        });
      } catch {
        console.error('CustomerIO handleEvents failed');
      }
    },
    trackEvent: (eventName: string, data: Record<string, unknown>) => {
      try {
        // @ts-ignore
        _cio.track(eventName, data);
      } catch {
        Sentry.addBreadcrumb({
          message: `CustomerIO trackEvent failed for event: ${eventName}`,
          category: 'customerIO',
          level: 'error',
        });

        console.error(`CustomerIO trackEvent failed for event: ${eventName}`);
      }
    },
  },
  [CustomerIOMode.OFF]: {
    identifyUser: () => {},
    resetUser: () => {},
    handleEvents: () => {},
    trackEvent: (eventName: string) => {
      console.log('DEV MODE: customer.io event should be tracking', eventName);
    },
  },
};

const CustomerIO = isProduction()
  ? customerIOMap[CustomerIOMode.ON]
  : customerIOMap[CustomerIOMode.OFF];

export const useCustomerIO = () => {
  const user = useCommonStore.use.user();

  const initCustomerIO = useCallback(() => {
    if (!user?.id) return;
    CustomerIO.identifyUser(user.id);
    CustomerIO.handleEvents();
  }, [user]);

  const resetCustomerIO = useCallback(() => {
    CustomerIO.resetUser();
  }, []);

  const trackCustomEvent = useCallback(
    (eventName: string) => {
      if (!user?.id) return;
      CustomerIO.trackEvent(eventName, { user_id: user.id });
    },
    [user]
  );
  return { initCustomerIO, resetCustomerIO, trackCustomEvent };
};
